import React from 'react';
import { filterReviewableProofProposals, isAcceptableProofProposal } from './controlProofFilter';
import './ControlProofProposals.css';

export type RegistryProposal = {
  proposal_id: string;
  kind: string;
  title: string;
  body?: string;
  cite?: string;
  evidence_note?: string;
  confidence?: number;
  control_id?: string;
  status?: string;
  source?: {
    portal_id?: string;
    self_serve_url?: string;
    credential_type?: string;
    attempted?: boolean;
    path?: string;
    name?: string;
    mode?: string;
    right_to_transact?: string | boolean | null;
    actions?: Array<{ id?: string; label?: string }>;
  };
};

type Props = {
  controlId: string;
  proposals: RegistryProposal[];
  language: 'en' | 'es';
  gatewayBaseUrl?: string;
  accessToken?: string;
  readOnly?: boolean;
  onChanged?: () => void;
  /** Open Gates and credentials with this portal id prefilled. */
  onAddCredentials?: (portalId: string) => void;
};

const kindLabel = (kind: string, es: boolean): string => {
  if (kind === 'possible_proof') return es ? 'Posible prueba' : 'Possible proof';
  if (kind === 'connector_evidence') return es ? 'Conector' : 'Connector';
  if (kind === 'needs_credentials') return es ? 'Configuración / auto-servicio' : 'Setup / self-serve';
  if (kind === 'obligation_watch') return es ? 'Aviso de obligación' : 'Obligation notice';
  if (kind === 'smart_solution') return es ? 'Solución sugerida' : 'Suggested solution';
  return kind;
};

/**
 * “Found possible proof” strip on a control card — accept/reject only;
 * never marks the checklist step done. Credential gaps offer library + self-serve.
 */
export default function ControlProofProposals({
  controlId,
  proposals,
  language,
  gatewayBaseUrl,
  accessToken,
  readOnly = false,
  onChanged,
  onAddCredentials,
}: Props): React.ReactElement | null {
  const es = language === 'es';
  const gateway = String(gatewayBaseUrl || '').replace(/\/$/, '');
  const [busyId, setBusyId] = React.useState('');
  const [error, setError] = React.useState('');
  // Hide staged Do-it-for-me / smart_solution drafts. Accept only for real evidence.
  const open = filterReviewableProofProposals(proposals, controlId);

  if (!open.length) return null;

  const decide = async (proposalId: string, decision: 'accepted' | 'rejected' | 'dismissed') => {
    if (readOnly || !accessToken || !gateway) return;
    setBusyId(proposalId);
    setError('');
    try {
      const response = await fetch(
        `${gateway}/api/v1/compliance-registry/proposals/${encodeURIComponent(proposalId)}/decide`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ decision }),
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { detail?: string };
        setError(typeof body.detail === 'string' ? body.detail : (es ? 'No se pudo registrar.' : 'Could not record.'));
        return;
      }
      onChanged?.();
    } catch {
      setError(es ? 'No se pudo registrar.' : 'Could not record.');
    } finally {
      setBusyId('');
    }
  };

  const openSelfServe = (url: string) => {
    const target = String(url || '').trim();
    if (!target || typeof window === 'undefined') return;
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      className="control-proof-proposals"
      aria-label={es ? 'Pruebas posibles del agente' : 'Agent possible proof'}
    >
      <h4 className="control-proof-proposals__title">
        {open.every((row) => row.kind === 'needs_credentials')
          ? (open.every((row) => {
            const portalId = String(row.source?.portal_id || '').trim();
            return portalId === 'tx_comptroller'
              || String(row.source?.credential_type || '') === 'api_key'
              || /franchise|comptroller/i.test(String(row.title || ''));
          })
            ? (es ? 'Búsqueda pública o auto-servicio' : 'Public search or self-serve')
            : (es ? 'Configuración o auto-servicio' : 'Setup or self-serve'))
          : (es ? 'Prueba lista para revisar' : 'Proof ready to review')}
        <span className="control-proof-proposals__count">{open.length}</span>
      </h4>
      {error ? <p className="control-proof-proposals__error" role="alert">{error}</p> : null}
      <ul className="control-proof-proposals__list">
        {open.map((row) => {
          const needsCreds = row.kind === 'needs_credentials';
          const canAccept = !needsCreds && isAcceptableProofProposal(row);
          const portalId = String(row.source?.portal_id || '').trim();
          const selfServeUrl = String(row.source?.self_serve_url || row.cite || '').trim();
          const isComptrollerPublic = portalId === 'tx_comptroller'
            || String(row.source?.credential_type || '') === 'api_key'
            || /comptroller|franchise/i.test(String(row.title || ''));
          return (
            <li key={row.proposal_id} className={needsCreds ? 'is-needs-credentials' : undefined}>
              <div className="control-proof-proposals__meta">
                <span className="control-proof-proposals__kind">{kindLabel(row.kind, es)}</span>
              </div>
              <strong>{row.title}</strong>
              {needsCreds && isComptrollerPublic ? (
                <p>
                  {es
                    ? 'La búsqueda de estado de franquicia es pública. No inicia sesión como contribuyente. NextCompli usa su clave API del servidor o el bot de búsqueda pública; o usted abre la búsqueda oficial y sube el PDF.'
                    : 'Franchise account status is a public search. You do not log in as a taxpayer. NextCompli uses its server API key or the public search bot — or you open the official search and upload the PDF.'}
                </p>
              ) : null}
              {needsCreds && row.evidence_note ? (
                <p className="control-proof-proposals__note">{row.evidence_note}</p>
              ) : null}
              {!needsCreds && row.body ? <p>{row.body}</p> : null}
              {!needsCreds && row.cite ? (
                <p className="control-proof-proposals__cite">
                  {es ? 'Fuente' : 'Cite'}: {row.cite}
                </p>
              ) : null}
              {!needsCreds && row.evidence_note ? (
                <p className="control-proof-proposals__note">
                  {es ? 'Nota de evidencia' : 'Evidence note'}: {row.evidence_note}
                </p>
              ) : null}
              {!readOnly && accessToken && gateway ? (
                <div className="control-proof-proposals__actions">
                  {needsCreds ? (
                    <>
                      {selfServeUrl ? (
                        <button
                          type="button"
                          className="dashboard-btn dashboard-btn-primary"
                          disabled={Boolean(busyId)}
                          onClick={() => {
                            openSelfServe(selfServeUrl);
                            void decide(row.proposal_id, 'dismissed');
                          }}
                        >
                          {es ? 'Abrir búsqueda pública' : 'Open public search'}
                        </button>
                      ) : null}
                      {!isComptrollerPublic ? (
                        <button
                          type="button"
                          className="dashboard-btn"
                          disabled={Boolean(busyId)}
                          onClick={() => {
                            onAddCredentials?.(portalId || 'tx_comptroller');
                          }}
                        >
                          {es ? 'Agregar a biblioteca de credenciales' : 'Add to credentials library'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="dashboard-btn"
                          disabled={Boolean(busyId)}
                          onClick={() => {
                            onAddCredentials?.(portalId || 'tx_comptroller');
                          }}
                        >
                          {es ? 'Clave API del servidor (Gates)' : 'Server API key (Gates)'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="dashboard-btn"
                        disabled={Boolean(busyId)}
                        onClick={() => void decide(row.proposal_id, 'rejected')}
                      >
                        {es ? 'Rechazar' : 'Reject'}
                      </button>
                    </>
                  ) : canAccept ? (
                    <>
                      <button
                        type="button"
                        className="dashboard-btn dashboard-btn-primary"
                        disabled={Boolean(busyId)}
                        onClick={() => void decide(row.proposal_id, 'accepted')}
                      >
                        {busyId === row.proposal_id ? (es ? '…' : '…') : (es ? 'Aceptar prueba' : 'Accept proof')}
                      </button>
                      <button
                        type="button"
                        className="dashboard-btn"
                        disabled={Boolean(busyId)}
                        onClick={() => void decide(row.proposal_id, 'rejected')}
                      >
                        {es ? 'Rechazar' : 'Reject'}
                      </button>
                      <button
                        type="button"
                        className="dashboard-btn"
                        disabled={Boolean(busyId)}
                        onClick={() => void decide(row.proposal_id, 'dismissed')}
                      >
                        {es ? 'Descartar' : 'Dismiss'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="dashboard-btn"
                      disabled={Boolean(busyId)}
                      onClick={() => void decide(row.proposal_id, 'dismissed')}
                    >
                      {es ? 'Descartar' : 'Dismiss'}
                    </button>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
